/* ==========================================================================
   global.js — theme utilities, sticky header, predictive search
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------- utilities ------------------------------ */

  const focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
    '[tabindex]:not([tabindex^="-"])',
  ].join(',');

  window.getFocusableElements = function (container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter(isVisible);
  };

  function isVisible(element) {
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }
  window.isVisible = isVisible;

  window.trapFocus = function (container, elementToFocus) {
    if (!container) return;
    elementToFocus = elementToFocus || container;
    const handleFocusIn = (event) => {
      if (container.contains(event.target)) return;
      event.stopPropagation();
      container.focus();
    };
    const handleFocusOut = (event) => {
      if (container.contains(event.relatedTarget)) return;
      event.stopPropagation();
      elementToFocus.focus();
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    container.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Tab') return;
        const focusable = window.getFocusableElements(container);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      { once: false }
    );
  };

  window.removeTrapFocus = function (elementToFocus, container) {
    document.removeEventListener('focusin', () => {});
    document.removeEventListener('focusout', () => {});
    if (elementToFocus) elementToFocus.focus();
    if (!container) return;
  };

  window.debounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  window.throttle = function (fn, delay) {
    let timeoutId;
    let lastExecTime = 0;
    return function (...args) {
      const context = this;
      const currentTime = Date.now();
      const execute = () => {
        lastExecTime = currentTime;
        fn.apply(context, args);
      };
      if (currentTime - lastExecTime >= delay) {
        execute();
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(execute, delay - (currentTime - lastExecTime));
      }
    };
  };

  window.onKeyUpEscape = function (event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') return true;
    return false;
  };

  /* pub/sub */
  const subscribers = {};
  window.subscribe = function (eventName, callback) {
    if (!subscribers[eventName]) subscribers[eventName] = [];
    subscribers[eventName].push(callback);
  };
  window.publish = function (eventName, data) {
    if (!subscribers[eventName]) return;
    subscribers[eventName].forEach((callback) => callback(data || {}));
  };

  window.fetchConfig = {
    type: 'json',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  };

  window.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.moneyFormat || '${{amount}}';
    function defaultTo(valueToFormat, defaultValue) {
      return valueToFormat == null || valueToFormat !== valueToFormat ? defaultValue : valueToFormat;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  };

  /* ------------------------------ StickyHeader ---------------------------- */

  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.setHeaderHeight();
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);

      this.onScrollHandler = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScrollHandler, false);
      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer;
      const handleIntersect = (entries, observerInstance) => {
        this.headerBounds = entries[0].intersectionRect;
        observerInstance.disconnect();
      };
      observer = new IntersectionObserver(handleIntersect, {
        rootMargin: this.headerIsAlwaysSticky ? '-10px 0px -200px' : '-1px 0px -200px',
        threshold: 0,
      });
      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        if (this.preventReveal) {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        } else {
          requestAnimationFrame(this.reveal.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        requestAnimationFrame(this.reset.bind(this));
      }
      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'animate'
      );
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal) this.searchModal.close(false);
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class StaticHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.setHeaderHeight();
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }
  }
  customElements.define('static-header', StaticHeader);

  /* --------------------------- Predictive search -------------------------- */

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.isOpen = false;

      this.setupEventListeners();
    }

    setupEventListeners() {
      if (!this.input) return;
      const form = this.querySelector('form.search');
      if (form) {
        form.addEventListener('submit', this.onFormSubmit.bind(this));
      }
      this.input.addEventListener('input', window.debounce(this.onChange.bind(this), 500));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.close();
        return;
      }
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"]')) event.preventDefault();
    }

    onFocus() {
      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.onChange();
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    }

    onKeydown(event) {
      if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return;
      const cursor = this.getQuery().length - 1;
      event.preventDefault();
      const options = this.querySelectorAll('li[id^="predictive-search-results"]');
      if (!options.length) return;
      let selectedOption;
      for (const option of options) {
        if (option.getAttribute('aria-selected') === 'true') selectedOption = option;
      }
      if (event.code === 'ArrowDown') {
        const next = selectedOption ? selectedOption.nextElementSibling : this.querySelector('li[id^="predictive-search-results"]');
        if (next) {
          selectedOption && selectedOption.setAttribute('aria-selected', 'false');
          next.setAttribute('aria-selected', 'true');
        }
      } else {
        const prev = selectedOption ? selectedOption.previousElementSibling : this.querySelector('li[id^="predictive-search-results"]:last-child');
        if (prev) {
          selectedOption && selectedOption.setAttribute('aria-selected', 'false');
          prev.setAttribute('aria-selected', 'true');
        }
      }
      const active = this.querySelector('li[aria-selected="true"] a');
      if (active) active.focus();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search`
      )
        .then((response) => {
          if (!response.ok) {
            const error = new Error(response.status);
            this.close();
            throw error;
          }
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      if (!this.statusElement) return;
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
      setTimeout(() => {
        this.setAttribute('loading', 'false');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      if (!this.statusElement) return;
      this.setLiveRegionText(this.statusElement.textContent || '');
      this.querySelectorAll('li[aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
    }

    open() {
      this.setAttribute('open', true);
      this.isOpen = true;
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'block';
    }

    close() {
      this.removeAttribute('open');
      this.isOpen = false;
      if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------- Header menu disclosure ----------------------- */

  class HeaderMenu extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        details: this.querySelector('details'),
        summary: this.querySelector('summary'),
      };
      if (!this.elements.details || !this.elements.summary) return;
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.elements.summary.addEventListener('click', this.onSummaryClick.bind(this));
      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
    }

    onSummaryClick(event) {
      event.preventDefault();
      const isOpen = this.elements.details.hasAttribute('open');
      isOpen ? this.close() : this.open();
    }

    onMouseEnter() {
      if (window.matchMedia('(hover: hover)').matches) {
        clearTimeout(this.debounceTimer);
        this.open();
      }
    }

    onMouseLeave() {
      if (window.matchMedia('(hover: hover)').matches) {
        this.debounceTimer = setTimeout(() => this.close(), 300);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    }

    open() {
      this.elements.details.setAttribute('open', '');
      const summary = this.elements.summary;
      summary.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden-tablet');
    }

    close() {
      this.elements.details.removeAttribute('open');
      const summary = this.elements.summary;
      summary.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden-tablet');
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ------------------------------ init ------------------------------------ */

  document.addEventListener('shopify:section:load', (event) => {
    // re-run any section-local initialization if needed
  });
})();
